// AP-162 / AP-167 · Guia rápido "Adicionar à tela inicial" (sem PWA).
// Totalmente localizado via usePortalI18n (ht / pt / en / fr / es).
import React, { useEffect, useState } from "react";
import { Smartphone, Share2, MoreVertical, Apple, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { usePortalI18n } from "./PortalI18nContext";

const STORAGE_KEY = "jms_portal_install_hint_v1";

export function useInstallGuide() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY);
      if (!dismissed) {
        const t = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);
  const show = () => setOpen(true);
  const close = () => setOpen(false);
  const dismissForever = () => {
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };
  return { open, show, close, dismissForever };
}

export default function PortalInstallGuide({ open, onClose, onDismiss }) {
  const { t } = usePortalI18n();
  const [os, setOs] = useState("android");
  useEffect(() => {
    if (!open) return;
    try {
      const ua = (navigator.userAgent || "").toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) setOs("ios");
      else setOs("android");
    } catch { /* ignore */ }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="portal-install-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            {t("install.title")}
          </DialogTitle>
          <DialogDescription>
            {t("install.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 justify-center" data-testid="install-os-toggle">
          <Button size="sm" variant={os === "android" ? "default" : "outline"}
                  onClick={() => setOs("android")} data-testid="install-os-android">
            Android
          </Button>
          <Button size="sm" variant={os === "ios" ? "default" : "outline"}
                  onClick={() => setOs("ios")} data-testid="install-os-ios">
            iPhone
          </Button>
        </div>

        {os === "android" ? (
          <div className="rounded-lg border bg-emerald-50/60 border-emerald-200 p-4 space-y-3 text-sm"
               data-testid="install-steps-android">
            <p className="font-semibold text-emerald-800">{t("install.android")}</p>
            <ol className="space-y-2 list-none">
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-emerald-600 text-white text-xs grid place-items-center font-semibold">1</span>
                <span><MoreVertical className="inline h-4 w-4 mb-0.5 text-slate-700" /> {t("install.androidStep1")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-emerald-600 text-white text-xs grid place-items-center font-semibold">2</span>
                <span>{t("install.androidStep2")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-emerald-600 text-white text-xs grid place-items-center font-semibold">3</span>
                <span>{t("install.androidStep3")}</span>
              </li>
            </ol>
          </div>
        ) : (
          <div className="rounded-lg border bg-sky-50/60 border-sky-200 p-4 space-y-3 text-sm"
               data-testid="install-steps-ios">
            <p className="font-semibold text-sky-800 flex items-center gap-1">
              <Apple className="h-4 w-4" /> {t("install.ios")}
            </p>
            <ol className="space-y-2 list-none">
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-sky-600 text-white text-xs grid place-items-center font-semibold">1</span>
                <span><Share2 className="inline h-4 w-4 mb-0.5 text-sky-700" /> {t("install.iosStep1")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-sky-600 text-white text-xs grid place-items-center font-semibold">2</span>
                <span>{t("install.iosStep2")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-sky-600 text-white text-xs grid place-items-center font-semibold">3</span>
                <span>{t("install.iosStep3")}</span>
              </li>
            </ol>
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onDismiss} data-testid="install-dismiss">
            <X className="h-4 w-4 mr-1" /> {t("install.dismiss")}
          </Button>
          <Button onClick={onClose} data-testid="install-ok">
            {t("install.ok")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
